import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { CheckCircle2, ArrowLeft, Loader2, Shield, Zap } from "lucide-react";
import { useForm } from "react-hook-form";

interface CheckoutFormData {
    nome: string;
    email: string;
    whatsapp: string;
    url: string;
}

interface PlanDetails {
    id: string;
    name: string;
    price: number;
    description: string;
    features: string[];
}

const plans: Record<string, PlanDetails> = {
    diagnostico: {
        id: "diagnostico",
        name: "Análise Completa",
        price: 89.90,
        description: "Relatório detalhado de AEO, performance, SEO e acessibilidade",
        features: [
            "Análise AEO completa",
            "Performance (PageSpeed Insights)",
            "SEO on-page e técnico",
            "Acessibilidade (WCAG)",
            "Melhores práticas web",
            "Relatório em PDF detalhado",
            "Entrega em até 48 horas"
        ]
    },
    manual: {
        id: "manual",
        name: "Análise + Guia de Implementação",
        price: 129.90,
        description: "Análise completa + guia técnico passo a passo para implementar todas as melhorias",
        features: [
            "Tudo do plano Análise Completa",
            "Guia técnico de implementação",
            "Passo a passo detalhado de cada melhoria",
            "Priorização de tarefas por impacto",
            "Exemplos de código e implementações",
            "Checklist de otimização AEO",
            "Suporte por email por 7 dias"
        ]
    }
};

export default function Checkout() {
    const { planId } = useParams<{ planId: string }>();
    const navigate = useNavigate();
    const [isProcessing, setIsProcessing] = useState(false);
    const [isRedirecting, setIsRedirecting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const plan = planId ? plans[planId] : null;

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<CheckoutFormData>();

    useEffect(() => {
        if (!plan) {
            navigate("/diagnostico-completo");
        }
    }, [plan, navigate]);

    const onSubmit = async (data: CheckoutFormData) => {
        setIsProcessing(true);
        setError(null);

        try {
            const response = await fetch('/api/create-checkout-session.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    planId: plan?.id,
                    customerData: data,
                }),
            });

            const result = await response.json();

            if (result.success && result.checkoutUrl) {
                // Mostrar estado de redirecionamento
                setIsRedirecting(true);
                setError(null);
                // Pequeno delay para mostrar o loading
                setTimeout(() => {
                    window.location.href = result.checkoutUrl;
                }, 800);
            } else {
                throw new Error(result.message || 'Erro ao processar pagamento');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erro ao processar. Tente novamente.');
            setIsProcessing(false);
        }
    };

    if (!plan) {
        return null;
    }

    return (
        <div className="min-h-screen bg-background relative overflow-hidden">
            {/* Loading Overlay durante redirecionamento */}
            {isRedirecting && (
                <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-[100] flex items-center justify-center">
                    <Card className="max-w-md border-primary/20">
                        <CardContent className="pt-6 text-center">
                            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                                <Loader2 className="h-8 w-8 text-primary animate-spin" />
                            </div>
                            <h3 className="text-xl font-semibold mb-2">Redirecionando para Pagamento</h3>
                            <p className="text-muted-foreground mb-4">
                                Você será redirecionado para o Stripe, nosso processador de pagamentos seguro.
                            </p>
                            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                                <Shield className="h-4 w-4 text-primary" />
                                <span>Conexão criptografada e segura</span>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Background elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-20 left-10 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
                <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
            </div>

            {/* Header */}
            <header className="border-b border-border/50 bg-card/30 backdrop-blur-xl sticky top-0 z-50 relative">
                <div className="container mx-auto px-4 py-4 flex justify-between items-center">
                    <Link to="/">
                        <img src="/logo.png" alt="Coddar Logo" className="h-11 w-auto" />
                    </Link>
                    <Button asChild variant="ghost">
                        <Link to="/diagnostico-completo">
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Voltar
                        </Link>
                    </Button>
                </div>
            </header>

            {/* Main Content */}
            <div className="container mx-auto px-4 py-12 relative z-10">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-8">
                        <h1 className="text-3xl md:text-4xl font-bold mb-2">
                            Finalizar Compra
                        </h1>
                        <p className="text-muted-foreground mb-4">
                            Preencha seus dados para continuar
                        </p>
                        
                        {/* Trust Badges */}
                        <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2">
                                <Shield className="h-4 w-4 text-green-600" />
                                <span>Pagamento Seguro</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                                <span>SSL Criptografado</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Zap className="h-4 w-4 text-primary" />
                                <span>Entrega em 48h</span>
                            </div>
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-3 gap-8">
                        {/* Formulário */}
                        <div className="lg:col-span-2">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Informações do Cliente</CardTitle>
                                    <CardDescription>
                                        Precisamos desses dados para enviar seu relatório
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                                        {/* Nome */}
                                        <div className="space-y-2">
                                            <Label htmlFor="nome">Nome Completo *</Label>
                                            <Input
                                                id="nome"
                                                placeholder="Seu nome completo"
                                                {...register("nome", {
                                                    required: "Nome é obrigatório",
                                                    minLength: { value: 3, message: "Nome muito curto" }
                                                })}
                                                disabled={isProcessing}
                                            />
                                            {errors.nome && (
                                                <p className="text-sm text-destructive">{errors.nome.message}</p>
                                            )}
                                        </div>

                                        {/* Email */}
                                        <div className="space-y-2">
                                            <Label htmlFor="email">Email *</Label>
                                            <Input
                                                id="email"
                                                type="email"
                                                placeholder="seu@email.com"
                                                {...register("email", {
                                                    required: "Email é obrigatório",
                                                    pattern: {
                                                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                                        message: "Email inválido"
                                                    }
                                                })}
                                                disabled={isProcessing}
                                            />
                                            {errors.email && (
                                                <p className="text-sm text-destructive">{errors.email.message}</p>
                                            )}
                                        </div>

                                        {/* WhatsApp */}
                                        <div className="space-y-2">
                                            <Label htmlFor="whatsapp">WhatsApp (com DDD) *</Label>
                                            <Input
                                                id="whatsapp"
                                                placeholder="(13) 99999-9999"
                                                {...register("whatsapp", {
                                                    required: "WhatsApp é obrigatório",
                                                    pattern: {
                                                        value: /^[\d\s\(\)\-\+]+$/,
                                                        message: "WhatsApp inválido"
                                                    }
                                                })}
                                                disabled={isProcessing}
                                            />
                                            {errors.whatsapp && (
                                                <p className="text-sm text-destructive">{errors.whatsapp.message}</p>
                                            )}
                                        </div>

                                        {/* URL */}
                                        <div className="space-y-2">
                                            <Label htmlFor="url">URL do seu site *</Label>
                                            <Input
                                                id="url"
                                                placeholder="https://seusite.com.br"
                                                {...register("url", {
                                                    required: "URL é obrigatória",
                                                    pattern: {
                                                        value: /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/,
                                                        message: "URL inválida"
                                                    }
                                                })}
                                                disabled={isProcessing}
                                            />
                                            {errors.url && (
                                                <p className="text-sm text-destructive">{errors.url.message}</p>
                                            )}
                                        </div>

                                        {/* Error Message */}
                                        {error && (
                                            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-sm text-destructive">
                                                {error}
                                            </div>
                                        )}

                                        {/* Submit Button */}
                                        <Button
                                            type="submit"
                                            size="lg"
                                            className="w-full"
                                            disabled={isProcessing}
                                        >
                                            {isProcessing ? (
                                                <>
                                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                    Redirecionando para pagamento seguro...
                                                </>
                                            ) : (
                                                <>
                                                    <Shield className="mr-2 h-4 w-4" />
                                                    Ir para Pagamento Seguro
                                                </>
                                            )}
                                        </Button>

                                        {/* Mensagem de Segurança */}
                                        <div className="bg-muted/50 border border-border rounded-lg p-4 text-center">
                                            <div className="flex items-center justify-center gap-2 mb-2">
                                                <Shield className="h-4 w-4 text-primary" />
                                                <p className="text-sm font-medium">Pagamento 100% Seguro</p>
                                            </div>
                                            <p className="text-xs text-muted-foreground">
                                                Você será redirecionado para o Stripe, nosso processador de pagamentos. 
                                                Seus dados de cartão são criptografados e nunca passam pelos nossos servidores.
                                            </p>
                                        </div>

                                        {/* Badges de Confiança */}
                                        <div className="flex items-center justify-center gap-4 pt-2">
                                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                <Shield className="h-3 w-3" />
                                                <span>SSL Seguro</span>
                                            </div>
                                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                <CheckCircle2 className="h-3 w-3" />
                                                <span>Powered by Stripe</span>
                                            </div>
                                        </div>
                                    </form>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Resumo do Pedido */}
                        <div className="lg:col-span-1">
                            <Card className="sticky top-24">
                                <CardHeader>
                                    <CardTitle>Resumo do Pedido</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div>
                                        <h3 className="font-semibold text-lg mb-2">{plan.name}</h3>
                                        <p className="text-sm text-muted-foreground mb-4">
                                            {plan.description}
                                        </p>
                                    </div>

                                    <Separator />

                                    <div className="space-y-2">
                                        <h4 className="font-medium text-sm">O que está incluído:</h4>
                                        <ul className="space-y-2">
                                            {plan.features.map((feature, index) => (
                                                <li key={index} className="flex items-start gap-2 text-sm">
                                                    <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                                                    <span className="text-muted-foreground">{feature}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    <Separator />

                                    <div className="space-y-2">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-muted-foreground">Subtotal</span>
                                            <span>R$ {plan.price.toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between font-bold text-lg">
                                            <span>Total</span>
                                            <span className="text-primary">R$ {plan.price.toFixed(2)}</span>
                                        </div>
                                    </div>

                                    <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                                        <div className="flex items-start gap-3">
                                            <Zap className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                            <div className="text-sm">
                                                <p className="font-medium text-foreground mb-1">Entrega Rápida</p>
                                                <p className="text-muted-foreground">
                                                    Você receberá seu relatório completo em até 48 horas após a confirmação do pagamento.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950 border border-green-200 dark:border-green-800 rounded-lg p-4">
                                        <div className="flex items-start gap-3">
                                            <Shield className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                                            <div className="text-sm">
                                                <p className="font-medium text-green-900 dark:text-green-100 mb-1">Garantia de Reembolso</p>
                                                <p className="text-green-700 dark:text-green-300 text-xs">
                                                    Se você contratar nossos serviços de desenvolvimento, 
                                                    o valor investido é 100% descontado do projeto final.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
